import { fetchImageUrlsBulk } from '../services/imageUploadService';
import { fetchImageViaProxy } from '../services/imageProxyClient';

// Shared DOM pre-processing helpers for the PDF exporters (server + raster).
//
// These used to be duplicated by hand across the PDF exporters (with a "mirror
// any change by hand" comment that was a standing future-bug magnet). They
// live here once and are imported by the exporters that need them.
// All operate on a DETACHED clone — they never mutate the live editor DOM.

// Wait for every <img> in `el` to finish loading (or error) so its src/natural
// size is resolved before serialising/snapshotting. Bounded by `timeoutMs` so a
// single stuck image can't hang the export.
export async function waitForImages(el: HTMLElement, timeoutMs = 8000): Promise<void> {
    const imgs = Array.from(el.querySelectorAll('img')) as HTMLImageElement[];
    if (imgs.length === 0) return;
    const settle = (img: HTMLImageElement) =>
        new Promise<void>((resolve) => {
            if (img.complete && img.naturalWidth > 0) return resolve();
            const done = () => {
                img.removeEventListener('load', done);
                img.removeEventListener('error', done);
                resolve();
            };
            img.addEventListener('load', done);
            img.addEventListener('error', done);
        });
    await Promise.race([Promise.all(imgs.map(settle)), new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
}

// Fetch a URL's bytes and return them as a base64 data: URI. Shared by both the
// raster exporter (exportToPdf.ts re-exports this) and the server inliner below
// so there is ONE implementation.
//
// The direct `credentials: 'omit'` fetch works only when the host sends CORS
// headers. That holds for most org S3 buckets (open CORS rule) but NOT all —
// some `*-aica-private-assets-prod` buckets lack it, and remote images
// (googleusercontent) never send CORS. On any such failure we fall back to the
// server-side image proxy (PDF lambda `{ url }` route), which fetches the bytes
// with no CORS restriction. Throws only when BOTH the direct fetch and the proxy
// fail, so the caller can log the gap.
export async function urlToDataUrl(url: string): Promise<string> {
    try {
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
        return await blobToDataUrl(await res.blob());
    } catch (err) {
        const viaProxy = await fetchImageViaProxy(url);
        if (viaProxy) return viaProxy;
        throw err;
    }
}

function blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Inline every <img> as a base64 data: URI. Required by the server exporter: the
// Lambda blocks all non-data:/about: network fetches during render
// (PdfHandler.renderPdf request interception), so an <img> still pointing at a
// presigned S3 URL is aborted and renders blank. Done on the detached clone so
// the live editor is untouched.
//
// This mirrors the proven raster path (exportToPdf.ts resolveDocIdImagesToBase64):
// it walks the DURABLE `data-doc-id` set — NOT the live `<img src>` — and re-mints
// FRESH presigned URLs at export time. The presigned URL baked into the live DOM
// at editor-mount is cached only ~10 min, so by export time it is usually EXPIRED
// (403). Re-minting makes the inline robust to expiry, and walking the docId set
// (not the rendered `<img>` set) also dodges the placeholder race. Images with no
// `data-doc-id` (unsaved base64 images — already data:, or a remote non-docId URL)
// fall back to fetching their live `src` directly.
export async function inlineImagesToDataUris(root: HTMLElement): Promise<void> {
    const imgs = Array.from(root.querySelectorAll('img')) as HTMLImageElement[];

    // 1. Re-mint fresh presigned URLs for every persisted (docId-backed) image.
    const docIdSet = new Set<string>();
    imgs.forEach((img) => {
        const id = img.getAttribute('data-doc-id');
        if (id) docIdSet.add(id);
    });

    let freshUrlByDocId: Record<string, string> = {};
    if (docIdSet.size > 0) {
        freshUrlByDocId = await fetchImageUrlsBulk(Array.from(docIdSet));
    }

    // 2. Resolve each image to a data: URI: prefer the fresh presigned URL minted
    //    from data-doc-id; fall back to the live src for non-docId images.
    await Promise.all(
        imgs.map(async (img) => {
            const docId = img.getAttribute('data-doc-id') || '';
            const liveSrc = img.getAttribute('src') || '';

            // An unsaved base64 image is already a data: URI — nothing to inline.
            if (!docId && liveSrc.startsWith('data:')) return;

            const fetchUrl = docId ? freshUrlByDocId[docId] : liveSrc;
            if (!fetchUrl || !/^https?:/i.test(fetchUrl)) {
                if (docId) {
                    console.warn('[pdf-export] image inline failed', {
                        docId,
                        src: liveSrc,
                        status: 'no presigned URL',
                    });
                }
                return;
            }

            try {
                const dataUrl = await urlToDataUrl(fetchUrl);
                img.setAttribute('src', dataUrl);
                img.removeAttribute('srcset');
            } catch (err: any) {
                const status = /fetch failed: (\d+)/.exec(String(err?.message || ''))?.[1] ?? String(err?.message || err);
                console.warn('[pdf-export] image inline failed', { docId: docId || undefined, src: fetchUrl, status });
            }
        }),
    );
}

export function stripInteractiveChrome(root: HTMLElement) {
    const selectors = [
        '[data-resize-handle]',
        '[class*="ResizeHandle"]',
        '[class*="FloatingToolbar"]',
        '[data-slate-placeholder]',
        '[data-plate-toolbar]',
        // Image editor chrome — the wrap toolbar, the "Uploading…" chip / error
        // overlay, the resize handles, and the delete (×) button must never
        // appear in the export.
        '[class*="ImageWrapMenu"]',
        '[class*="ImageElement_uploadChip"]',
        '[class*="ImageElement_uploadError"]',
        '[class*="ImageElement_handle"]',
        '[class*="ImageElement_deleteBtn"]',
    ];
    for (const sel of selectors) {
        root.querySelectorAll(sel).forEach((n) => n.remove());
    }
    // Drop transient selection classes so their blue ::after / ring overlay
    // can't render: the cell-selection tint AND the image selection border.
    root.querySelectorAll('[class*="cellSelected"], [class*="imageContainerSelected"]').forEach((el) => {
        const kept = Array.from((el as HTMLElement).classList).filter((c) => !/cellSelected|imageContainerSelected/i.test(c));
        (el as HTMLElement).className = kept.join(' ');
    });
}

// Convert each table's px-based <col> widths to percentages so the exported
// table keeps the editor's column proportions while fitting the page width.
// Plate renders <colgroup><col style="width: Npx"> from its stored colSizes
// (sized for the ~1229px editor); on a narrower page those absolute px widths
// either overflow or get ignored under table-layout: auto. Percentages keep the
// proportions exact at any page width.
export function normalizeTableColumnsForPrint(root: HTMLElement) {
    const tables = Array.from(root.querySelectorAll('table'));
    for (const table of tables) {
        const cols = Array.from(table.querySelectorAll('colgroup > col')) as HTMLElement[];
        if (cols.length === 0) continue;
        const widths = cols.map((col) => {
            const styleW = parseFloat(col.style.width || '');
            const attrW = parseFloat(col.getAttribute('width') || '');
            const w = Number.isFinite(styleW) && styleW > 0 ? styleW : Number.isFinite(attrW) && attrW > 0 ? attrW : 0;
            return w;
        });
        const definedTotal = widths.reduce((sum, w) => sum + w, 0);
        if (definedTotal <= 0) continue;
        // Fill any zero-width (un-resized) columns with the average of the defined
        // ones so the percentages still sum sensibly to 100%.
        const definedCount = widths.filter((w) => w > 0).length;
        const avg = definedTotal / Math.max(definedCount, 1);
        const filled = widths.map((w) => (w > 0 ? w : avg));
        const total = filled.reduce((sum, w) => sum + w, 0);
        cols.forEach((col, i) => {
            const pct = (filled[i] / total) * 100;
            col.style.width = `${pct.toFixed(4)}%`;
            col.removeAttribute('width');
        });
    }
}

// Hide trailing empty paragraphs (TrailingBlockPlugin's caret-landing spot + any
// stray user-pressed-Enter empties at the document end) so the final page isn't
// one or two lines of whitespace.
export function trimTrailingEmptyBlocks(root: HTMLElement) {
    const blocks = Array.from(root.children).filter((c) => c instanceof HTMLElement) as HTMLElement[];
    while (blocks.length > 0) {
        const last = blocks[blocks.length - 1];
        const text = (last.textContent || '').trim();
        const hasMedia = last.querySelector('img, video, table, figure, iframe, hr');
        if (text.length === 0 && !hasMedia) {
            last.remove();
            blocks.pop();
        } else {
            break;
        }
    }
}

const MEDIA_SEL = 'img, video, table, figure, iframe, hr, svg, canvas';

// Remove trailing whitespace-only nodes. The editor stores the user's trailing
// blank lines as text nodes full of "\n"; under `white-space: pre-wrap` these
// render as tall empty space that spills into a blank final PDF page. We descend
// the last-child chain dropping pure-whitespace text nodes and empty inline
// wrappers, then trim trailing whitespace off the final text run.
//
// `hasMedia` matches the element ITSELF as well as its descendants. The self-match
// is essential: the descent can reach a bare <img> (a void image as the document's
// last block, once trimTrailingEmptyBlocks has dropped the trailing empty
// paragraph). querySelector only checks descendants, so without matches() the
// childless <img> looked "empty" and was removed — silently dropping a trailing
// image from the exported PDF. (Adding any element after the image moved the
// descent off the <img>, which is why a trailing image failed but image+text did
// not.)
export function trimTrailingWhitespace(root: HTMLElement) {
    const hasMedia = (el: Element) => el.matches(MEDIA_SEL) || el.querySelector(MEDIA_SEL) !== null;
    let node: Node | null = root;
    let guard = 0;
    while (node && guard++ < 100000) {
        const last: ChildNode | null = node.lastChild;
        if (!last) break;
        if (last.nodeType === Node.TEXT_NODE) {
            const text = last.textContent || '';
            if (!text.trim()) {
                last.remove();
                continue;
            }
            last.textContent = text.replace(/\s+$/, '');
            break;
        }
        if (last.nodeType === Node.ELEMENT_NODE) {
            const el = last as HTMLElement;
            if (!(el.textContent || '').trim() && !hasMedia(el)) {
                el.remove();
                continue;
            }
            node = el; // descend into the last non-empty element and keep trimming
            continue;
        }
        last.remove(); // comments / other
    }
}
